import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Text } from './Text';
import { spacing, palette } from '../../styles/tokens';

export interface PageHeaderBackLink {
  to: string;
  label: string;
}

interface PageHeaderProps {
  title: string;
  description?: string;
  primaryAction?: ReactNode;
  secondaryActions?: ReactNode;
  back?: PageHeaderBackLink;
}

export function PageHeader({
  title,
  description,
  primaryAction,
  secondaryActions,
  back,
}: PageHeaderProps) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      paddingBottom: spacing.lg,
      marginBottom: spacing.lg,
      borderBottom: `1px solid ${palette.g150}`,
      gap: spacing.lg,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {back && (
          <Link
            to={back.to}
            aria-label={`Back to ${back.label}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              fontWeight: 500,
              color: palette.g500,
              textDecoration: 'none',
              marginBottom: spacing.xs,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = palette.accent;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = palette.g500;
            }}
          >
            <ChevronLeft size={14} strokeWidth={2} />
            <span>Back to {back.label}</span>
          </Link>
        )}
        <Text variant="title" as="h1">{title}</Text>
        {description && (
          <Text
            variant="body"
            color="secondary"
            style={{ marginTop: spacing.xs }}
          >
            {description}
          </Text>
        )}
      </div>
      {(primaryAction || secondaryActions) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          flexShrink: 0,
        }}>
          {secondaryActions}
          {primaryAction}
        </div>
      )}
    </div>
  );
}
